import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Bot,
  Calendar,
  MessageSquare,
  User,
  Briefcase,
  ClipboardList,
  LogOut,
  Search,
  CheckCircle2,
  Building2,
  LayoutDashboard,
  Inbox,
} from 'lucide-react';
import { Avatar } from './common';
import ErrorBoundary from './ErrorBoundary';
import { useAuth } from '../context/AuthContext';
import { useActiveHoa } from '../features/hoa/hooks';
import { HOA_SUBNAV, HOA_ADMIN_SUBNAV } from '../features/hoa/constants';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, profileLoading, signOut } = useAuth();

  // HOA state — conditional nav
  const { hasHoa } = useActiveHoa();
  const onHoaRoute = location.pathname.startsWith('/hoa');

  const isPro = profile?.role === 'pro';
  const isHoaAdmin = profile?.role === 'hoa_admin';
  const isVerified = isPro && profile?.verification_status === 'verified';

  if (profileLoading && !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-brand-red border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const displayName = profile?.first_name
    ? `${profile.first_name} ${profile.last_name || ''}`.trim()
    : user?.email || 'User';

  const homeownerNav = [
    { name: 'Home', path: '/dashboard', icon: Home },
    { name: 'Homie', path: '/homie', icon: Bot },
    { name: 'My Requests', path: '/requests', icon: ClipboardList },
    { name: 'Messages', path: '/chat', icon: MessageSquare },
    { name: 'Bookings', path: '/bookings', icon: Calendar },
    { name: 'Profile', path: '/profile', icon: User },
  ];

  const proNav = [
    { name: 'Home', path: '/pro/dashboard', icon: Home },
    { name: 'Leads', path: '/pro/leads', icon: Search },
    { name: 'My Jobs', path: '/pro/jobs', icon: Briefcase },
    { name: 'Messages', path: '/pro/chat', icon: MessageSquare },
    { name: 'Profile', path: '/pro/profile', icon: User },
  ];

  const hoaAdminNav = [
    { name: 'HOA Dashboard', path: '/hoa-admin', icon: LayoutDashboard },
    { name: 'Triage Queue', path: '/hoa-admin', icon: Inbox },
    { name: 'Profile', path: '/profile', icon: User },
  ];

  const navigation = isPro ? proNav : isHoaAdmin ? hoaAdminNav : homeownerNav;

  // Where to insert "My HOA" tree: between Bookings and Profile
  const insertHoaAt = isPro
    ? -1
    : navigation.findIndex((i) => i.path === '/profile');

  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // HOA parent is active when on any /hoa route but NOT a specific sub-route,
  // or when sub-nav is collapsed (not on any /hoa route)
  const isHoaParentActive = location.pathname === '/hoa';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const homePath = isPro ? '/pro/dashboard' : isHoaAdmin ? '/hoa-admin' : '/dashboard';

  // Render a single nav item (desktop)
  const renderNavItem = (item, indent = false) => (
    <Link
      key={item.name}
      to={item.path}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-sm font-medium ${
        indent ? 'ml-4 py-2' : ''
      } ${
        isActive(item.path)
          ? 'bg-brand-red text-white shadow-md'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      <item.icon className={indent ? 'w-4 h-4' : 'w-5 h-5'} />
      <span>{item.name}</span>
    </Link>
  );

  // Mobile sub-nav items — "overview" dropped because:
  //  (1) tapping "HOA" in bottom tab bar already goes to overview
  //  (2) with 4 items (Events, Chat, Requests, Billing) the pills fit
  //      on a 375px viewport without horizontal scrolling
  const mobileSubNav = HOA_SUBNAV.filter((i) => i.id !== 'overview');

  // Whether the mobile HOA sub-nav tab bar is showing (affects content
  // padding below it)
  const showMobileSubNav = hasHoa && !isPro && !isHoaAdmin && onHoaRoute;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between h-14 px-4">
          <Link to={homePath} className="flex items-center gap-2">
            <img src="/homecierge_logo.svg" alt="Homecierge" className="w-8 h-8 rounded-xl" />
            <span className="font-bold text-lg text-brand-charcoal">Homecierge</span>
            {isPro && (
              <span className="ml-1 px-2 py-0.5 bg-brand-charcoal text-white text-xs font-bold rounded-full">
                PRO
              </span>
            )}
          </Link>
        </div>
      </header>

      <div className="flex h-screen pt-14 pb-16 lg:pt-0 lg:pb-0">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-200">
          <div className="flex items-center gap-3 h-20 px-6 border-b border-gray-200">
            <Link to={homePath} className="flex items-center gap-3">
              <img src="/homecierge_logo.svg" alt="Homecierge" className="w-9 h-9 rounded-xl shadow-md" />
              <span className="font-bold text-xl text-brand-charcoal">Homecierge</span>
              {isPro && (
                <span className="px-2 py-0.5 bg-brand-charcoal text-white text-xs font-bold rounded-full">
                  PRO
                </span>
              )}
            </Link>
          </div>

          <div className="px-4 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Avatar name={displayName} size="md" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-semibold text-brand-charcoal truncate">{displayName}</p>
                  {isVerified && (
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" aria-label="Verified" />
                  )}
                </div>
                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500 mt-0.5">
                  {isPro ? 'Pro' : isHoaAdmin ? 'HOA Admin' : 'Homeowner'}
                </span>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="flex flex-col gap-2">
              {navigation.map((item, index) => {
                const nodes = [renderNavItem(item)];

                // Insert "My HOA" + its expanded children before Profile (homeowners only)
                if (hasHoa && !isPro && !isHoaAdmin && index === insertHoaAt - 1) {
                  nodes.push(
                    <Link
                      key="my-hoa"
                      to="/hoa"
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-sm font-medium ${
                        isHoaParentActive
                          ? 'bg-brand-red text-white shadow-md'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Building2 className="w-5 h-5" />
                      <span>My HOA</span>
                    </Link>
                  );

                  // Expanded sub-nav when on any /hoa route
                  if (onHoaRoute) {
                    HOA_SUBNAV.forEach((sub) => {
                      if (sub.id === 'overview') return; // parent covers this
                      nodes.push(
                        <Link
                          key={sub.id}
                          to={sub.path}
                          className={`flex items-center gap-3 ml-4 px-4 py-2 rounded-lg transition-colors text-sm ${
                            location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')
                              ? 'bg-red-50 text-brand-red font-semibold'
                              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                          }`}
                        >
                          <sub.icon className="w-4 h-4" />
                          <span>{sub.name}</span>
                        </Link>
                      );
                    });

                    // Admin sub-nav — only when user is in admin mode.
                    // Separator line + admin items rendered below the
                    // resident sub-nav items so the resident experience
                    // stays visually primary.
                    if (isHoaAdmin) {
                      nodes.push(
                        <div
                          key="admin-divider"
                          className="ml-4 mt-2 mb-1 px-2 flex items-center gap-2"
                        >
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                            Admin
                          </span>
                          <span className="flex-1 h-px bg-gray-200" />
                        </div>
                      );
                      HOA_ADMIN_SUBNAV.forEach((sub) => {
                        const adminActive =
                          location.pathname === sub.path ||
                          location.pathname.startsWith(sub.path + '/');
                        nodes.push(
                          <Link
                            key={sub.id}
                            to={sub.path}
                            className={`flex items-center gap-3 ml-4 px-4 py-2 rounded-lg transition-colors text-sm ${
                              adminActive
                                ? 'bg-brand-charcoal text-white font-semibold'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            }`}
                          >
                            <sub.icon className="w-4 h-4" />
                            <span>{sub.name}</span>
                          </Link>
                        );
                      });
                    }
                  }
                }

                return nodes;
              })}
            </div>
          </nav>

          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors font-medium text-sm"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </aside>

        <div className="flex-1 overflow-y-auto">
          {/* Extra top padding on mobile when the HOA sub-nav tab bar is
              rendering, so page content doesn't slide under it. */}
          <main className={`min-h-full ${showMobileSubNav ? 'pt-12 lg:pt-0' : ''}`}>
            {/* Scoped to the routed panel — a route that throws leaves the
                sidebar and tab bar usable. Keyed on the path so navigating
                away clears the error. */}
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>

      {/* Mobile Bottom Tab Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200">
        <div className="flex items-center justify-around h-16 px-1">
          {(() => {
            // Build mobile tabs with optional My HOA
            const tabs = [...navigation];
            if (hasHoa && !isPro && !isHoaAdmin) {
              const profileIdx = tabs.findIndex((i) => i.path === '/profile');
              tabs.splice(profileIdx, 0, { name: 'HOA', path: '/hoa', icon: Building2 });
            }
            return tabs.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-1 transition-colors ${
                    active ? 'text-brand-red' : 'text-gray-400'
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                  <span className="text-[10px] font-medium truncate max-w-full px-1">{item.name}</span>
                </Link>
              );
            });
          })()}
        </div>
      </nav>

      {/* Mobile HOA sub-nav: pill tab bar when on /hoa/*.
          Overview is not included — bottom tab bar "HOA" covers that. */}
      {showMobileSubNav && (
        <div className="lg:hidden fixed top-14 left-0 right-0 z-40 bg-white border-b border-gray-200 overflow-x-auto">
          <div className="flex items-center gap-1 px-3 py-2 whitespace-nowrap">
            {mobileSubNav.map((sub) => {
              const active = location.pathname.startsWith(sub.path);
              return (
                <Link
                  key={sub.id}
                  to={sub.path}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    active
                      ? 'bg-brand-red text-white'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <sub.icon className="w-3.5 h-3.5" />
                  <span>{sub.name}</span>
                </Link>
              );
            })}
            {isHoaAdmin && HOA_ADMIN_SUBNAV.map((sub) => {
              const active = location.pathname.startsWith(sub.path);
              return (
                <Link
                  key={sub.id}
                  to={sub.path}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    active
                      ? 'bg-brand-charcoal text-white'
                      : 'text-brand-charcoal bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <sub.icon className="w-3.5 h-3.5" />
                  <span>{sub.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
